import { outcomesOf, isRange, topOf } from 'shared/decision/routing';

/**
 * A Decision model step's edges ⇄ the shared route table.
 *
 * Edge (what the server walks)                                   Route (what the editor shows)
 *   { route_id, name, when: { field, op: 'in', value }, to }       { id, name, when: { field: [..] }, to }
 *   { ..., when: { field, op: 'between', range }, to }             a score range { min, max }
 *   { ..., when: { all: [cond, cond] }, to }                       one condition per question
 *   { default: to }                                                 otherwise: { name, to }
 *
 * A score range is min ≤ value < max, and ≤ max at the top of the scale,
 * the same reading as routing.js. Edges are tried in order and the first
 * match wins, on the server (edges.chooseEdge) as in the coverage check.
 */

function setOf(cond, q) {
  const all = outcomesOf(q);
  const v = cond.value;
  switch (cond.op) {
    case 'in': return all.filter((o) => Array.isArray(v) && v.includes(o));
    case 'eq': return all.filter((o) => o === v);
    case 'not_in': return all.filter((o) => !(Array.isArray(v) && v.includes(o)));
    case 'neq': return all.filter((o) => o !== v);
    default: return null;
  }
}

function rangeOfCond(cond, q) {
  const top = topOf(q);
  switch (cond.op) {
    case 'between': return { min: Number(cond.range?.min ?? 0), max: Number(cond.range?.max ?? top) };
    case 'gte': case 'gt': return { min: Number(cond.value), max: top };
    case 'lt': case 'lte': return { min: 0, max: Number(cond.value) };
    default: return null;
  }
}

/** Edges → { routes, otherwise }. */
export function edgesToTable(edges, questions) {
  const routes = [];
  let otherwise = { name: 'Otherwise', to: null };
  (edges || []).forEach((e, i) => {
    if (e.default !== undefined) { otherwise = { name: 'Otherwise', to: e.default }; return; }
    const parts = e.any_answer ? [] : (e.when && Array.isArray(e.when.all) ? e.when.all : (e.when ? [e.when] : []));
    const when = {};
    for (const c of parts) {
      const q = c && questions[c.field];
      if (!q) continue;
      if (isRange(q)) {
        const r = rangeOfCond(c, q);
        if (!r) continue;
        const was = when[c.field];
        when[c.field] = was ? { min: Math.max(was.min, r.min), max: Math.min(was.max, r.max) } : r;
      } else {
        const s = setOf(c, q);
        if (s) when[c.field] = when[c.field] ? when[c.field].filter((o) => s.includes(o)) : s;
      }
    }
    routes.push({ id: e.route_id || `route_e${i}`, name: e.name || `Route ${routes.length + 1}`, when, to: e.to || null });
  });
  return { routes, otherwise };
}

function condFor(field, value, q) {
  if (isRange(q)) {
    const top = topOf(q);
    const r = value && !Array.isArray(value) ? value : { min: 0, max: top };
    return { field, op: 'between', range: { min: r.min, max: r.max, inclusive_min: true, inclusive_max: r.max >= top } };
  }
  return { field, op: 'in', value };
}

/** One route's conditions as an edge condition. */
function compile(when, questions) {
  const parts = Object.entries(when || {})
    .filter(([k]) => questions[k])
    .map(([field, value]) => condFor(field, value, questions[field]));
  if (!parts.length) {
    const [field] = Object.keys(questions);
    const q = questions[field];
    return condFor(field, isRange(q) ? null : outcomesOf(q), q);
  }
  return parts.length === 1 ? parts[0] : { all: parts };
}

/** { routes, otherwise } → edges (routes in order, the default last). */
export function tableToEdges(table, questions) {
  const edges = (table.routes || []).map((r) => {
    const any = !Object.keys(r.when || {}).some((k) => questions[k]);
    // A route with no condition matches every answer; it is written as "the
    // first question is any answer" and marked, so it reads back empty.
    return { route_id: r.id, name: r.name, when: compile(r.when, questions), to: r.to || null, ...(any ? { any_answer: true } : {}) };
  });
  edges.push({ default: (table.otherwise && table.otherwise.to) || null });
  return edges;
}

/**
 * Questions changed: keep every route, drop conditions on removed questions
 * and outcomes that no longer exist. A route stays where it was so its
 * target and order survive an edit.
 */
export function repairRoutes(edges, questions) {
  return tableToEdges(edgesToTable(edges, questions), questions);
}
